import React, { useEffect, useState } from 'react';
import { SkeletonText, Dropdown, TooltipIcon } from 'carbon-components-react';
import {
  HelpFilled16,
  CheckmarkFilled16,
  Information16
} from '@carbon/icons-react';
import { typicalWaitTimeFormatter } from 'utilities/formatters';
import { phoneNumberFormatter } from 'utilities/validation';
import { SCREEN_SIZE } from 'types';
import TextWithLinks from 'components/TextWithLinks';

export const ReferralsList: React.FC<Props | any> = (props: Props) => {
  const {
    referrals,
    screenSize,
    isLoading,
    handleContactsSelection,
    enquiryValidityDate,
    setSelectedReferralOverview
  } = props;
  const [dropDownItems, setDropDownItems] = useState<any[]>([]);

  const handleContacts = (selected: string) => {
    handleContactsSelection(selected);
  };

  useEffect(() => {
    if (Array.isArray(referrals)) {
      const items = referrals.map((referral) => {
        const options = [];
        if (referral.contactPhoneNumber) {
          options.push({
            label: (
              <div className="items">
                PHONE (GENERAL)
                <div>
                  {phoneNumberFormatter({
                    phoneNumber: referral.contactPhoneNumber
                  }).getNationalNumberFormat()}
                </div>
              </div>
            ),
            value: `phone|${referral.contactPhoneNumber}`
          });
        }

        if (referral.contactEmail) {
          options.push({
            label: (
              <div>
                EMAIL (GENERAL)
                <div className="email">{referral.contactEmail}</div>
              </div>
            ),
            value: `email|${referral.contactEmail}|${referral.externalId}`
          });
        }

        if (referral.contactUrl) {
          options.push({
            label: (
              <div>
                WEB (GENERAL)
                <div>Opens in a new tab</div>
              </div>
            ),
            value: `web|${referral.contactUrl}`
          });
        }

        if (referral.contactEmergencyPhone) {
          options.push({
            label: (
              <div style={{ color: 'red' }}>
                EMERGENCY
                <div>
                  {phoneNumberFormatter({
                    phoneNumber: referral.contactEmergencyPhone
                  }).getNationalNumberFormat()}
                </div>
              </div>
            ),
            value: `emergency|${referral.contactEmergencyPhone}`
          });
        }

        return options;
      });

      setDropDownItems(items);
    }
  }, [referrals]);

  return (
    <div className="referrals-list">
      <div className="referrals-list-validity">
        {!isLoading && (
          <>
            <Information16 />
            {`This enquiry is valid until ${enquiryValidityDate}`}
          </>
        )}
        {isLoading && <SkeletonText />}
      </div>
      {!isLoading &&
        referrals?.map((referral: any, index: any) => (
          <div className="referrals-list-listing" key={index}>
            {referral?.notices?.length > 0 && referral?.status !== 'COMPLETED' && (
              <div className="referrals-list-listing-announcement">
                {referral.notices.map((notice: any, index: any) => (
                  <div
                    className="referrals-list-listing-announcement-content"
                    key={index}
                  >
                    <div className="referrals-list-listing-announcement-content-title">
                      {notice.title}
                    </div>
                    <div
                      className="referrals-list-listing-announcement-content-text"
                      dangerouslySetInnerHTML={{ __html: notice.text }}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="referrals-list-listing-body">
              <div
                className="referrals-list-listing-body-content"
                onClick={() => setSelectedReferralOverview(index)}
              >
                <div className="referrals-list-listing-body-content-header">
                  <div className="referrals-list-listing-body-content-header-name">
                    {referral?.name}
                  </div>
                  <div className="referrals-list-listing-body-content-header-wait-time">
                    {referral?.status === 'COMPLETED' ? (
                      <>
                        READY
                        <TooltipIcon
                          tooltipText={'Response received'}
                          className="referrals-list-listing-body-content-header-ready-tooltip enquiry-details-tooltip"
                          align={
                            screenSize === SCREEN_SIZE.SM ? 'center' : 'center'
                          }
                        >
                          <CheckmarkFilled16 className="referrals-list-listing-body-content-header-ready-tooltip-icon" />
                        </TooltipIcon>
                      </>
                    ) : (
                      <>
                        Typical wait{' '}
                        {typicalWaitTimeFormatter(referral?.waittime)}{' '}
                        <TooltipIcon
                          tooltipText={
                            'Typical wait times are approximate. They are calculated from how long it took this member to respond to most enquiries in the past two weeks.'
                          }
                          className="referrals-list-listing-body-content-header-wait-time-tooltip enquiry-details-tooltip"
                          align={
                            screenSize === SCREEN_SIZE.SM ? 'center' : 'center'
                          }
                        >
                          <HelpFilled16 className="referrals-list-listing-body-content-header-wait-time-tooltip-icon" />
                        </TooltipIcon>
                      </>
                    )}
                  </div>
                </div>

                <TextWithLinks
                  className={`referrals-list-listing-body-content-safety-message ${
                    referral?.notices?.length > 0 && 'with-announcement'
                  }`}
                  text={
                    referral?.responses?.length > 0 &&
                    referral?.responses[0]?.custom_properties?.safetyMessage
                      ? referral?.responses[0]?.custom_properties?.safetyMessage
                      : referral?.safetyMessage
                  }
                />
              </div>
              <div className="referrals-list-listing-body-contacts">
                <Dropdown
                  id="category"
                  titleText=""
                  label="Contact"
                  size="xl"
                  className="referrals-list-listing-body-contacts-dropdown"
                  selectedItem={'Select'}
                  onChange={(item: any) =>
                    handleContacts(item?.selectedItem?.value)
                  }
                  disabled={
                    !Array.isArray(dropDownItems[index]) ||
                    dropDownItems[index].length === 0
                  }
                  items={dropDownItems[index] || []}
                />
              </div>
            </div>
          </div>
        ))}
      {isLoading &&
        [1].map((val: any) => (
          <div className="referrals-list-skeleton" key={val}>
            <div className="referrals-list-skeleton-body">
              <div className="referrals-list-skeleton-body-content">
                <div className="referrals-list-skeleton-body-content-header">
                  {isLoading && (
                    <div className="referrals-list-skeleton-body-content-header-name">
                      <SkeletonText />
                    </div>
                  )}
                </div>
                <div className="referrals-list-skeleton-body-content-safety-message">
                  {isLoading ? (
                    <SkeletonText />
                  ) : (
                    'No members are operating in this area, but there may still be non-member assets present'
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

type ownProps = {
  referrals: any;
  screenSize: string;
  isLoading: boolean;
  handleContactsSelection: Function;
  enquiryValidityDate: string;
  setSelectedReferralOverview: Function;
};

type Props = ownProps;

ReferralsList.displayName = 'ReferralsList';

export default React.memo(ReferralsList);
